import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const getCurrencySymbol = (currency) => {
  switch (currency) {
    case 'JPY': return '\u00a5';
    case 'EUR': return '\u20ac';
    case 'USD':
    default: return '$';
  }
};

const formatCurrency = (amount, currency) => {
  const symbol = getCurrencySymbol(currency);
  if (currency === 'JPY') {
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }
  return `${symbol}${Number(amount).toFixed(2)}`;
};

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * Generate a professional PDF from quote data
 * @param {Object} quoteData - The quote object with line_items, customer info, etc.
 * @param {Object} companySettings - Company settings (name, address, etc.)
 * @returns {Promise<void>}
 */
export async function generateQuotePDF(quoteData, companySettings) {
  const subtotal = (quoteData.line_items || []).reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0
  );
  const taxAmount = subtotal * ((quoteData.tax_rate || 0) / 100);
  const shippingCost = quoteData.include_shipping !== false ? (quoteData.shipping_cost || 0) : 0;
  const total = subtotal + shippingCost + taxAmount;
  const currency = quoteData.currency || 'USD';

  // Build image HTML
  const imagesHtml = (quoteData.images && quoteData.images.length > 0) ? `
    <div style="margin-bottom: 24px;">
      <h3 style="font-weight: bold; color: #374151; margin-bottom: 12px; font-size: 14px;">PRODUCT IMAGES</h3>
      <div style="display: flex; flex-wrap: wrap; gap: 12px;">
        ${quoteData.images.map((img, idx) => {
          const src = img.url?.startsWith('/') ? `${API_URL}${img.url}` : img.url;
          return `<div style="border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; width: 140px; background: #f9fafb;">
            <img src="${src}" alt="${img.name || `Product ${idx + 1}`}" style="width: 140px; height: 100px; object-fit: cover;" crossorigin="anonymous" />
            <p style="font-size: 10px; text-align: center; padding: 4px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${img.name || ''}</p>
          </div>`;
        }).join('')}
      </div>
    </div>
  ` : '';

  // Build line items rows
  const lineItemsHtml = (quoteData.line_items || []).map((item, idx) => `
    <tr style="background: ${idx % 2 === 0 ? '#f9fafb' : '#ffffff'};">
      <td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${item.item_type || ''}</td>
      <td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; font-weight: 600;">${item.item_name || ''}</td>
      <td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; white-space: pre-wrap; max-width: 200px;">${item.description || ''}</td>
      <td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: center;">${item.quantity || 0}</td>
      <td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">${formatCurrency(item.unit_price || 0, currency)}</td>
      <td style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right; font-weight: 600;">${formatCurrency((item.quantity || 0) * (item.unit_price || 0), currency)}</td>
    </tr>
  `).join('');

  const htmlContent = `
    <div style="font-family: 'Helvetica Neue', Arial, sans-serif; width: 760px; padding: 40px; background: #ffffff; color: #1f2937;">
      
      <!-- Header -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 16px; border-bottom: 3px solid #7c3aed;">
        <div>
          <h1 style="font-size: 22px; font-weight: 800; color: #1f2937; margin: 0 0 4px 0;">${companySettings.company_name || 'Company Name'}</h1>
          ${companySettings.company_name_local ? `<p style="font-size: 13px; color: #6b7280; margin: 0;">${companySettings.company_name_local}</p>` : ''}
        </div>
        <div style="text-align: right;">
          <h2 style="font-size: 18px; font-weight: 700; color: #7c3aed; margin: 0 0 4px 0;">PROFORMA INVOICE</h2>
          <p style="font-size: 12px; color: #6b7280; margin: 0;">Quote #: ${quoteData.quote_number || 'NEW'}</p>
          <p style="font-size: 12px; color: #6b7280; margin: 2px 0 0 0;">Date: ${new Date(quoteData.created_at || Date.now()).toLocaleDateString()}</p>
        </div>
      </div>

      <!-- Company & Customer Info -->
      <div style="display: flex; gap: 24px; margin-bottom: 28px;">
        <div style="flex: 1; padding: 16px; background: #f3f4f6; border-radius: 8px;">
          <h3 style="font-size: 11px; font-weight: 700; color: #6b7280; letter-spacing: 0.5px; margin: 0 0 8px 0;">SELLER</h3>
          <p style="font-size: 13px; font-weight: 600; margin: 0 0 2px 0;">${companySettings.company_name || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">${companySettings.contact_person || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">${companySettings.address || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">Tel: ${companySettings.phone || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">Email: ${companySettings.email || ''}</p>
          ${companySettings.whatsapp ? `<p style="font-size: 12px; color: #4b5563; margin: 2px 0;">WhatsApp: ${companySettings.whatsapp}</p>` : ''}
        </div>
        <div style="flex: 1; padding: 16px; background: #f5f3ff; border-radius: 8px;">
          <h3 style="font-size: 11px; font-weight: 700; color: #6b7280; letter-spacing: 0.5px; margin: 0 0 8px 0;">BUYER</h3>
          <p style="font-size: 13px; font-weight: 600; margin: 0 0 2px 0;">${quoteData.customer_company || quoteData.customer_name || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">${quoteData.customer_contact || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">${quoteData.customer_address || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">Tel: ${quoteData.customer_phone || ''}</p>
          <p style="font-size: 12px; color: #4b5563; margin: 2px 0;">Email: ${quoteData.customer_email || ''}</p>
          ${quoteData.customer_whatsapp ? `<p style="font-size: 12px; color: #4b5563; margin: 2px 0;">WhatsApp: ${quoteData.customer_whatsapp}</p>` : ''}
        </div>
      </div>

      <!-- Line Items -->
      ${quoteData.separate_items && quoteData.options && quoteData.options.length > 0 ? `
        <!-- Separate Options Mode -->
        ${quoteData.options.map((opt, optIdx) => {
          const optSub = (opt.line_items || []).reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
          const optTax = optSub * ((opt.tax_rate || 0) / 100);
          const optShip = opt.shipping_cost || 0;
          const optTotal = optSub + optTax + optShip;
          const optLineItemsHtml = (opt.line_items || []).map((item, idx) => `
            <tr style="background: ${idx % 2 === 0 ? '#f9fafb' : '#ffffff'};">
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${item.item_type || ''}</td>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 12px; font-weight: 600;">${item.item_name || ''}</td>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; white-space: pre-wrap;">${item.description || ''}</td>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: center;">${item.quantity || 0}</td>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">${formatCurrency(item.unit_price || 0, currency)}</td>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right; font-weight: 600;">${formatCurrency((item.quantity || 0) * (item.unit_price || 0), currency)}</td>
            </tr>
          `).join('');
          return `
          <div style="border: 2px solid #ddd6fe; border-radius: 8px; overflow: hidden; margin-bottom: 16px;">
            <div style="background: #7c3aed; color: #ffffff; padding: 8px 16px; font-weight: 700; font-size: 13px;">
              ${opt.name || `OPTION ${optIdx + 1}`}
            </div>
            <div style="padding: 16px;">
              <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px;">
                <thead>
                  <tr style="background: #f3f4f6;">
                    <th style="padding: 6px 10px; text-align: left; font-size: 10px; color: #6b7280;">TYPE</th>
                    <th style="padding: 6px 10px; text-align: left; font-size: 10px; color: #6b7280;">ITEM</th>
                    <th style="padding: 6px 10px; text-align: left; font-size: 10px; color: #6b7280;">DESCRIPTION</th>
                    <th style="padding: 6px 10px; text-align: center; font-size: 10px; color: #6b7280;">QTY</th>
                    <th style="padding: 6px 10px; text-align: right; font-size: 10px; color: #6b7280;">UNIT PRICE</th>
                    <th style="padding: 6px 10px; text-align: right; font-size: 10px; color: #6b7280;">TOTAL</th>
                  </tr>
                </thead>
                <tbody>${optLineItemsHtml}</tbody>
              </table>
              <div style="display: flex; justify-content: flex-end;">
                <div style="width: 240px;">
                  <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #e5e7eb;">
                    <span style="font-size: 12px; color: #6b7280;">Subtotal:</span>
                    <span style="font-size: 12px;">${formatCurrency(optSub, currency)}</span>
                  </div>
                  ${optShip > 0 ? `<div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #e5e7eb;">
                    <span style="font-size: 12px; color: #6b7280;">Shipping:</span>
                    <span style="font-size: 12px;">${formatCurrency(optShip, currency)}</span>
                  </div>` : ''}
                  ${(opt.tax_rate || 0) > 0 ? `<div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #e5e7eb;">
                    <span style="font-size: 12px; color: #6b7280;">Tax (${opt.tax_rate}%):</span>
                    <span style="font-size: 12px;">${formatCurrency(optTax, currency)}</span>
                  </div>` : ''}
                  <div style="display: flex; justify-content: space-between; padding: 8px 0;">
                    <span style="font-size: 14px; font-weight: 800; color: #7c3aed;">TOTAL:</span>
                    <span style="font-size: 14px; font-weight: 800; color: #7c3aed;">${formatCurrency(optTotal, currency)} ${currency}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>`;
        }).join('')}
        }).join('')}
      ` : `
        <!-- Combined Mode -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px;">
          <thead>
            <tr style="background: #7c3aed;">
              <th style="padding: 10px 12px; text-align: left; color: #ffffff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;">TYPE</th>
              <th style="padding: 10px 12px; text-align: left; color: #ffffff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;">ITEM</th>
              <th style="padding: 10px 12px; text-align: left; color: #ffffff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;">DESCRIPTION</th>
              <th style="padding: 10px 12px; text-align: center; color: #ffffff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;">QTY</th>
              <th style="padding: 10px 12px; text-align: right; color: #ffffff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;">UNIT PRICE</th>
              <th style="padding: 10px 12px; text-align: right; color: #ffffff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px;">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHtml}
          </tbody>
        </table>

        ${imagesHtml}

        <!-- Combined Totals -->
        <div style="display: flex; justify-content: flex-end; margin-bottom: 24px;">
          <div style="width: 280px;">
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
              <span style="font-size: 13px; color: #6b7280;">Subtotal:</span>
              <span style="font-size: 13px; font-weight: 500;">${formatCurrency(subtotal, currency)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
              <span style="font-size: 13px; color: #6b7280;">Shipping:</span>
              <span style="font-size: 13px; ${quoteData.include_shipping === false ? 'color: #9ca3af; font-style: italic;' : 'font-weight: 500;'}">
                ${quoteData.include_shipping !== false ? formatCurrency(quoteData.shipping_cost || 0, currency) : 'Not Included'}
              </span>
            </div>
            ${(quoteData.tax_rate || 0) > 0 ? `
              <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
                <span style="font-size: 13px; color: #6b7280;">Tax (${quoteData.tax_rate}%):</span>
                <span style="font-size: 13px; font-weight: 500;">${formatCurrency(taxAmount, currency)}</span>
              </div>
            ` : ''}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; margin-top: 4px;">
              <span style="font-size: 16px; font-weight: 800; color: #7c3aed;">TOTAL:</span>
              <span style="font-size: 16px; font-weight: 800; color: #7c3aed;">${formatCurrency(quoteData.include_shipping !== false ? total : (subtotal + taxAmount), currency)} ${currency}</span>
            </div>
          </div>
        </div>
      `}

      <!-- Quote Options -->
      <div style="display: flex; gap: 16px; margin-bottom: 24px; padding: 16px; background: #f5f3ff; border-radius: 8px;">
        <div style="flex: 1; text-align: center;">
          <p style="font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 4px 0;">Installation</p>
          <p style="font-size: 13px; font-weight: 600; color: #7c3aed; margin: 0;">${quoteData.installation_location === 'inside' ? 'Indoor' : 'Outdoor'}</p>
        </div>
        ${quoteData.mounting_style ? `
          <div style="flex: 1; text-align: center;">
            <p style="font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 4px 0;">Mounting</p>
            <p style="font-size: 13px; font-weight: 600; color: #7c3aed; margin: 0;">${quoteData.mounting_style}</p>
          </div>
        ` : ''}
        <div style="flex: 1; text-align: center;">
          <p style="font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 4px 0;">Warranty</p>
          <p style="font-size: 13px; font-weight: 600; color: #7c3aed; margin: 0;">${quoteData.include_warranty ? '1 Year Included' : 'Not Included'}</p>
        </div>
        <div style="flex: 1; text-align: center;">
          <p style="font-size: 10px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 4px 0;">Shipping</p>
          <p style="font-size: 13px; font-weight: 600; color: #7c3aed; margin: 0;">${quoteData.include_shipping !== false ? 'Included' : 'Not Included'}</p>
        </div>
      </div>

      <!-- Delivery & Payment -->
      <div style="display: flex; gap: 24px; margin-bottom: 24px;">
        <div style="flex: 1; padding: 16px; background: #f3f4f6; border-radius: 8px;">
          <h3 style="font-size: 12px; font-weight: 700; color: #374151; margin: 0 0 8px 0;">DELIVERY INFORMATION</h3>
          <p style="font-size: 12px; margin: 4px 0;"><strong>Processing:</strong> ${quoteData.processing_time || 'TBD'}</p>
          <p style="font-size: 12px; margin: 4px 0;"><strong>Delivery:</strong> ${quoteData.delivery_time || 'TBD'}</p>
          <p style="font-size: 12px; margin: 4px 0;"><strong>Method:</strong> ${quoteData.delivery_method || 'TBD'}</p>
        </div>
        <div style="flex: 1; padding: 16px; background: #f3f4f6; border-radius: 8px;">
          <h3 style="font-size: 12px; font-weight: 700; color: #374151; margin: 0 0 8px 0;">PAYMENT TERMS</h3>
          <p style="font-size: 12px; margin: 4px 0;">${quoteData.payment_terms || ''}</p>
          ${companySettings.paypal_email ? `<p style="font-size: 12px; margin: 4px 0;"><strong>PayPal:</strong> ${companySettings.paypal_email}</p>` : ''}
          ${companySettings.bank_details ? `<p style="font-size: 11px; margin: 8px 0 0 0; white-space: pre-wrap; color: #4b5563;">${companySettings.bank_details}</p>` : ''}
        </div>
      </div>

      ${quoteData.notes ? `
        <div style="padding: 16px; background: #fefce8; border-radius: 8px; border-left: 4px solid #eab308;">
          <h3 style="font-size: 12px; font-weight: 700; color: #374151; margin: 0 0 8px 0;">NOTES</h3>
          <p style="font-size: 12px; white-space: pre-wrap; margin: 0; color: #4b5563;">${quoteData.notes}</p>
        </div>
      ` : ''}

      <!-- Footer -->
      <div style="margin-top: 32px; padding-top: 16px; border-top: 2px solid #e5e7eb; text-align: center;">
        <p style="font-size: 11px; color: #9ca3af; margin: 0;">Thank you for your business | ${companySettings.company_name || ''} | ${companySettings.email || ''} | ${companySettings.phone || ''}</p>
      </div>
    </div>
  `;

  // Create temporary container off-screen
  const container = document.createElement('div');
  container.style.cssText = 'position: fixed; left: -9999px; top: 0; z-index: -1;';
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  // Wait for images to load
  const images = container.querySelectorAll('img');
  if (images.length > 0) {
    await Promise.allSettled(
      Array.from(images).map(img => new Promise((resolve) => {
        if (img.complete) { resolve(); return; }
        img.onload = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 3000);
      }))
    );
  }

  try {
    const element = container.firstElementChild;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdfWidth = 210;
    const pdfHeight = 297;
    const marginX = 8;
    const marginY = 8;
    const contentWidth = pdfWidth - (marginX * 2);
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = contentWidth / imgWidth;
    const scaledHeight = imgHeight * ratio;
    const maxContentHeight = pdfHeight - (marginY * 2);

    const pdf = new jsPDF('p', 'mm', 'a4');

    if (scaledHeight <= maxContentHeight) {
      pdf.addImage(imgData, 'JPEG', marginX, marginY, contentWidth, scaledHeight);
    } else {
      const pageCanvasHeight = maxContentHeight / ratio;
      let yOffset = 0;
      let pageNum = 0;

      while (yOffset < imgHeight) {
        if (pageNum > 0) pdf.addPage();
        const sliceHeight = Math.min(pageCanvasHeight, imgHeight - yOffset);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, yOffset, imgWidth, sliceHeight, 0, 0, imgWidth, sliceHeight);
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        const pageScaledHeight = sliceHeight * ratio;
        pdf.addImage(pageImgData, 'JPEG', marginX, marginY, contentWidth, pageScaledHeight);
        yOffset += sliceHeight;
        pageNum++;
      }
    }

    const quoteNum = quoteData.quote_number || quoteData.id?.slice(-6) || 'NEW';
    const customerName = (quoteData.customer_company || quoteData.customer_name || 'Quote').replace(/[^a-zA-Z0-9]/g, '_');
    pdf.save(`Quote_${quoteNum}_${customerName}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
